import React, { useEffect, useState, useContext } from "react";
import cx from "classnames";
import { useHistory } from "react-router-dom";
import styles from "../scss/DapQuestion.module.scss";
import DigitalAdviceQuestionForm from "./DigitalAdviceQuestionForm";
import {
    IDigitalQuestion,
    IInterimRecommendedCoverResults,
    IQuestionOptionImage
} from "../interfaces/IDigitalAdviceData";
import {
    deleteReponse,
    getDAPEachQuestion,
    getInterimRecommendedCover,
    getSessionId,
    setQuestionAnswer
} from "../api/ApiDigitalAdvice";
import Icon from "../../common/components/Icon/Icon";
import InterimRecommendedSection from "./InterimRecommendedSection";
import { AdviceControllerContext } from "../providers/DigitalAdviceProvider";
import ProgressBar from "../../common/components/ProgressBar/ProgressBar";
import useErrorLogger from "../../common/hooks/useErrorLogger";
import DapQestionPlaceholder from "./Placeholder/DapQuestionPlaceholder";
import { GenerateQuoteCategory } from "../enums/GenerateQuoteCategory";

export interface ISumbitParams {
    sessionUserId?: string;
    questionId: number;
    questionOptionId?: number;
    textValue?: string;
}

interface IDigitalAdviceQuestionProps {
    questionId: string;
    setProgressBarCurrentStage?: (
        progress: React.ComponentProps<typeof ProgressBar>["progressBarCurrentStage"]
    ) => void;
    addFrameImage?: (image: IQuestionOptionImage, isAdded: boolean) => void;
    addMultipleFrameImages?: (images: IQuestionOptionImage[], isAdded: boolean) => void;
    setBaseImages?: (images: IQuestionOptionImage[]) => void;
    setImagePlaceholders?: (images: IQuestionOptionImage[]) => void;
    resetFramePlaceholder?: () => void;
    setIsLoading?: (isLoading: boolean) => void;
    setFrameLabel?: (label: string) => void;
    setFrameImages?: (images: IQuestionOptionImage[]) => void;
    getFrameImages?: () => IQuestionOptionImage[];
    resetFrameImages?: () => void;
}

const DigitalAdviceQuestion: React.FC<IDigitalAdviceQuestionProps> = (props) => {
    const { questionId } = props;

    const history = useHistory();
    const search = new URLSearchParams(location.search);
    const [question, setQuestion] = useState<IDigitalQuestion>();
    const [questionIsReady, setQuestionIsReady] = useState(false);
    const [isFormSubmit, setIsFormSubmit] = useState(false);
    const [isBackQuestion, setIsBackQuestion] = useState(false);
    const [nextQuestionOrder, setNextQuestionOrder] = useState(0);
    const [previousQuestionId, setPreviousQuestionId] = useState(0);
    const [recommendedCover, setRecommendedCover] = useState<
        IInterimRecommendedCoverResults
    >();
    const [quoteGroupID, setQuoteGroupID] = useState("");
    const getParams = (): string => {
        return search.has("mode") && search.get("mode") === "app" ? "?mode=app" : "";
    };
    const [queryParams, setQueryParams] = useState(getParams());
    const { actions, selectors } = useContext(AdviceControllerContext);
    const [showRecommendation, setShowRecommendation] = useState(false);

    const setProgressStage = (sectionId): void => {
        if (!sectionId) {
            return;
        }
        switch (sectionId) {
            case 1:
                props.setProgressBarCurrentStage("why");
                break;
            case 2:
                props.setProgressBarCurrentStage("you");
                break;
            case 3:
                props.setProgressBarCurrentStage("finances");
                break;
            default:
        }
    };

    const navigateToOurAdvice = (params) => {
        history.push(
            `/digital-advice/our-advice?sessionUserId=${params.sessionUserId}&visitorId=${params.visitorId}`
        );
    };

    const handleSubmitAnswer = async (params: ISumbitParams): Promise<void> => {
        setIsFormSubmit(true);
        return setQuestionAnswer({
            ...params,
            sessionUserId: selectors.getSessionUserId()
        })
            .then((res) => {
                setPreviousQuestionId(res.previousQuestionId);
                actions.setPreviousQuestionId(res.previousQuestionId);
                if (res && res.id) {
                    setQuestion(res);
                    actions.setFrameImages({
                        questionId: res.id,
                        frameImages: props.getFrameImages()
                    });
                    history.push(`/digital-advice/${res.id}/question${queryParams}`);
                    setProgressStage(res.sectionId);
                    setShowRecommendation(false);
                } else {
                    navigateToOurAdvice({
                        sessionUserId: selectors.getSessionUserId(),
                        visitorId: selectors.getVisitorId()
                    });
                }
            })
            .catch(() => {
                useErrorLogger();
            })
            .finally(() => setIsFormSubmit(false));
    };

    const handleNextQuestion = async (questionOrderBy = 0): Promise<void> => {
        setIsFormSubmit(true);
        setShowRecommendation(false);
        if (questionOrderBy === -1) {
            navigateToOurAdvice({
                sessionUserId: selectors.getSessionUserId(),
                visitorId: selectors.getVisitorId()
            });
            return;
        }
        const orderBy = showRecommendation
            ? nextQuestionOrder || selectors.getNextQuestionOrder()
            : questionOrderBy || question.orderBy + 1;
        return getDAPEachQuestion({
            sessionUserId: selectors.getSessionUserId(),
            orderBy
        })
            .then((res) => {
                setPreviousQuestionId(res.previousQuestionId);
                actions.setPreviousQuestionId(res.previousQuestionId);
                if (res && res.id) {
                    setQuestion(res);
                    history.push(`/digital-advice/${res.id}/question${queryParams}`);
                    setProgressStage(res.sectionId);
                    if (!(res.baseImages && res.baseImages.length)) {
                        actions.setFrameImages({
                            questionId: res.id,
                            frameImages: props.getFrameImages()
                        });
                    }
                } else {
                    navigateToOurAdvice({
                        sessionUserId: selectors.getSessionUserId(),
                        visitorId: selectors.getVisitorId()
                    });
                }
            })
            .finally(() => setIsFormSubmit(false));
    };

    const handleMultipleSubmitAnswer = async (
        answerList: ISumbitParams[]
    ): Promise<void> => {
        setIsFormSubmit(true);
        if (!answerList || !answerList.length) {
            handleNextQuestion(0).catch(() => {
                useErrorLogger();
            });
            return;
        }
        const promises = [];
        answerList.forEach((item) => {
            const params = {
                questionId: item.questionId,
                textValue: item.textValue || null,
                questionOptionId: item.questionOptionId || null,
                sessionUserId: selectors.getSessionUserId()
            };
            promises.push(setQuestionAnswer(params));
        });
        await Promise.all(promises)
            .then((res) => {
                const previousQuestion = res.find((i) => i.previousQuestionId);
                if (previousQuestion) {
                    setPreviousQuestionId(previousQuestion.previousQuestionId);
                    actions.setPreviousQuestionId(previousQuestion.previousQuestionId);
                }

                const nextQuestion = res.find((i) => i.id);
                handleNextQuestion(nextQuestion?.orderBy || -1).catch(() => {
                    useErrorLogger();
                });
            })
            .catch(() => {
                useErrorLogger();
            });
    };

    const retrieveRecommendation = (): void => {
        getInterimRecommendedCover({
            sessionUserId: selectors.getSessionUserId(),
            visitorId: selectors.getVisitorId()
        })
            .then((res) => {
                setRecommendedCover(res);

                const hasFrameImages = !!(
                    props.getFrameImages() && props.getFrameImages().length
                );
                if (!hasFrameImages) {
                    props.setBaseImages(selectors.getFrameBaseImages());
                    props.setFrameImages(selectors.getFrameImages(-1));
                }
            })
            .catch(() => {
                handleNextQuestion(0).catch(() => {
                    useErrorLogger();
                });
            })
            .finally(() => props.setIsLoading(false));
    };

    const setQuestionInfo = (res): void => {
        props.setIsLoading(false);
        setQuestion(res);
        actions.setQuestion(res);
        setProgressStage(res.sectionId);
        props.setFrameLabel(res.frameLabel);
        setPreviousQuestionId(res.previousQuestionId);
        actions.setPreviousQuestionId(res.previousQuestionId);

        if (res.baseImages && res.baseImages.length) {
            props.resetFramePlaceholder();
            props.setBaseImages(res.baseImages);
            actions.setFrameBaseImages(res.baseImages);
        } else {
            props.setBaseImages(selectors.getFrameBaseImages());
        }
        if (res.placeholderImages && res.placeholderImages.length) {
            props.setImagePlaceholders(res.placeholderImages);
        }

        const hasFrameImages = !!(
            props.getFrameImages() && props.getFrameImages().length
        );
        if (!hasFrameImages) {
            props.setFrameImages(selectors.getFrameImages(res.id));
        }

        setQuestionIsReady(true);
        setIsFormSubmit(false);
    };

    const retrieveQuestion = (sessionId): void => {
        setQuestionIsReady(false);
        getDAPEachQuestion({
            sessionUserId: sessionId || selectors.getSessionUserId(),
            questionId: parseInt(questionId)
        })
            .then((res) => {
                setQuestionInfo(res);
            })
            .catch(() => {
                useErrorLogger();
            })
            .finally(() => {
                setQuestionIsReady(true);
                setIsFormSubmit(false);
            });
    };

    const handleBack = (): void => {
        setIsBackQuestion(true);
        history.goBack();
        setQuestionIsReady(false);
        setIsFormSubmit(true);
    };

    const showBackButton = (): boolean => {
        return (
            !queryParams || !!(previousQuestionId || selectors.getPreviousQuestionId())
        );
    };

    useEffect(() => {
        if (!questionId) {
            return;
        }
        window.localStorage.setItem("dap_process", "true");
        if (
            !quoteGroupID &&
            selectors.getDigitalQuoteGroupIDs(GenerateQuoteCategory.INTERIM)
        ) {
            setQuoteGroupID(
                selectors.getDigitalQuoteGroupIDs(GenerateQuoteCategory.INTERIM)
            );
        }
        if (isBackQuestion) {
            props.resetFrameImages();
            props.resetFramePlaceholder();
            deleteReponse({
                questionId: parseInt(questionId),
                sessionUserId: selectors.getSessionUserId()
            })
                .catch(() => {
                    useErrorLogger();
                })
                .finally(() => {
                    const frameImages = selectors.getFrameImages(parseInt(questionId));
                    if (!frameImages?.length) {
                        actions.resetFrameImages();
                    }
                    props.setFrameImages(frameImages);
                    setIsBackQuestion(false);
                });
        }
        setShowRecommendation(false);
        if (questionId === "interim-recommend") {
            setProgressStage(2);
            setShowRecommendation(true);
            retrieveRecommendation();
            setIsBackQuestion(false);
            return;
        }
        if (!selectors.getSessionUserId()) {
            getSessionId()
                .then((res) => {
                    if (res?.sessionUserId) {
                        actions.setSessionUserId(res?.sessionUserId);
                        actions.setVisitorId(res?.visitorId);
                        retrieveQuestion(res?.sessionUserId);
                    }
                })
                .catch(() => {
                    useErrorLogger();
                });
        } else {
            retrieveQuestion("");
        }
    }, [questionId]);

    return (
        <div className={styles["dap-question-page"]}>
            <div className={styles["back-btn-wrapper"]}>
                {questionIsReady && !isFormSubmit && showBackButton() && (
                    <button
                        className={styles["back-btn"]}
                        disabled={isBackQuestion}
                        onClick={() => {
                            handleBack();
                        }}>
                        <Icon
                            size="l"
                            className={cx(styles.icon)}
                            id="ChevronBackLarge"
                        />
                        Back
                    </button>
                )}
            </div>

            {(isBackQuestion || !questionIsReady || isFormSubmit) &&
                !showRecommendation && <DapQestionPlaceholder />}

            {!isBackQuestion && questionIsReady && !isFormSubmit && (
                <DigitalAdviceQuestionForm
                    question={question}
                    onSubmit={handleSubmitAnswer}
                    onMultiSubmit={handleMultipleSubmitAnswer}
                    showForm={!showRecommendation}
                    addFrameImage={props.addFrameImage}
                    addMultipleFrameImages={props.addMultipleFrameImages}
                    setImagePlaceholders={props.setImagePlaceholders}
                    resetFramePlaceholder={props.resetFramePlaceholder}
                />
            )}

            {showRecommendation && (
                <InterimRecommendedSection
                    next={handleNextQuestion}
                    recommendCover={recommendedCover}
                    quoteGroupID={quoteGroupID}
                />
            )}
        </div>
    );
};

export default DigitalAdviceQuestion;
